import type * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

export type TabScrollAffordanceState = {
  /** Attach to the element that wraps the CDS <Tabs>; scroll geometry is read from within it. */
  tabsWrapperRef: React.RefObject<HTMLDivElement>;
  canScrollBackward: boolean;
  canScrollForward: boolean;
  scrollBackward: () => void;
  scrollForward: () => void;
};

/**
 * Tracks horizontal overflow of a CDS tab bar and exposes controls to scroll it.
 * CDS <Tabs> render as a scrollable MUI tablist with scrollButtons="off", so this fills
 * the pointer gap: it measures the scroller and reports whether tabs remain off either edge.
 *
 * Self-contained — it re-measures on its own (scroll, window resize, and a ResizeObserver on
 * the scroller/tablist so it reacts when the set of tabs changes), so consumers only attach
 * the ref and render the controls.
 */
export const useTabScrollAffordance = (): TabScrollAffordanceState => {
  const tabsWrapperRef = useRef<HTMLDivElement>(null);
  const [canScrollForward, setCanScrollForward] = useState(false);
  const [canScrollBackward, setCanScrollBackward] = useState(false);

  const getTabScroller = useCallback<() => HTMLElement | null>(() => {
    const wrapper = tabsWrapperRef.current;
    if (!wrapper) return null;
    // MUI's generated class names aren't stable across dev/prod/SSR (the app's JSS config
    // strips the global MuiTabs-scroller class), so anchor to the structural tablist —
    // role is class-independent — and take its parent, which is the scroll container.
    const tablist = wrapper.querySelector<HTMLElement>('[role="tablist"]');
    return tablist?.parentElement ?? wrapper.querySelector<HTMLElement>('[class*="MuiTabs-scroller"]');
  }, []);

  const remeasure = useCallback<() => void>(() => {
    const scroller = getTabScroller();
    if (!scroller) return;
    // abs() handles RTL's negative scrollLeft; scrolled = distance already past the leading edge
    const scrolled = Math.abs(scroller.scrollLeft);
    const remaining = scroller.scrollWidth - scroller.clientWidth - scrolled;
    setCanScrollForward(remaining > 1);
    setCanScrollBackward(scrolled > 1);
  }, [getTabScroller]);

  useEffect(() => {
    remeasure();
    const scroller = getTabScroller();
    if (!scroller) return undefined;

    scroller.addEventListener('scroll', remeasure, { passive: true });
    window.addEventListener('resize', remeasure);

    // Re-measure when the scroller or its tab row changes size — e.g. a gated tab appears,
    // the Payment tab loads, or the container reflows. ResizeObserver is absent in jsdom.
    let observer: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(() => remeasure());
      observer.observe(scroller);
      const tablist = scroller.querySelector('[role="tablist"]');
      if (tablist) observer.observe(tablist);
    }

    return () => {
      scroller.removeEventListener('scroll', remeasure);
      window.removeEventListener('resize', remeasure);
      observer?.disconnect();
    };
  }, [getTabScroller, remeasure]);

  const scrollByViewport = useCallback<(sign: 1 | -1) => void>(
    (sign) => {
      const scroller = getTabScroller();
      if (!scroller) return;
      const direction = scroller.scrollLeft < 0 ? -1 : 1; // RTL scrolls negative
      scroller.scrollBy({ left: sign * direction * scroller.clientWidth * 0.8, behavior: 'smooth' });
    },
    [getTabScroller]
  );

  const scrollForward = useCallback<() => void>(() => scrollByViewport(1), [scrollByViewport]);
  const scrollBackward = useCallback<() => void>(() => scrollByViewport(-1), [scrollByViewport]);

  return { tabsWrapperRef, canScrollBackward, canScrollForward, scrollBackward, scrollForward };
};
